import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Cpu, TrendingUp, Search as SearchIcon, Users, Target, LineChart, BarChart3, Bot, ArrowRight, CheckCircle2, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useSEO } from "../hooks/useSEO";
import { useJsonLd, personSchema, breadcrumb } from "../hooks/useJsonLd";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-100px" }, transition: { duration: 0.6 } };

const services = [
  {
    id: "ai-marketing-consulting",
    icon: Cpu,
    name: "AI Marketing Consulting",
    tagline: "Turn AI from buzzword into a working growth system.",
    who: "Founders and CMOs experimenting with AI but not seeing ROI.",
    problems: [
      "AI tools bought, no compounding output",
      "Team can't stitch tools into a workflow",
      "No system for tracking AI-driven lift",
    ],
    approach: [
      "Audit current marketing stack + AI usage",
      "Design 3–5 autonomous or assisted workflows",
      "Build → deploy → measure lift on a 90-day loop",
    ],
    outcomes: ["30–60% reduction in reporting overhead", "Faster budget decisions", "AI workflows owned by the team"],
    faqs: [
      { q: "Do I need engineers to work with you?", a: "No. I ship no-code + light-code stacks (n8n, Make, Zapier + OpenAI). If code is truly needed, I bring it." },
      { q: "How is this different from prompting?", a: "This is systems work — retrieval, guardrails, evals, dashboards. Prompting is a feature, not the product." },
    ],
    accent: "#3B82F6",
    featured: true,
  },
  {
    id: "performance-marketing",
    icon: TrendingUp,
    name: "Performance Marketing",
    tagline: "Predictable, profitable paid acquisition.",
    who: "D2C, OTT and B2B teams scaling past $10K/mo ad spend.",
    problems: ["ROAS plateau", "Rising CAC", "Creative fatigue", "Attribution confusion"],
    approach: ["Audit spend → find leaks", "Rebuild targeting, creative and offer", "Full-funnel measurement (GA4 + platform)"],
    outcomes: ["5–7x average ROAS", "20–40% CAC reduction", "Compounding creative library"],
    faqs: [
      { q: "Which platforms do you run?", a: "Google Ads, Meta Ads, Amazon DSP, DV360, Taboola, Outbrain, MGID." },
      { q: "Do you handle media buying end-to-end?", a: "Yes — strategy + hands-on-keyboard through Aiadspace." },
    ],
  },
  {
    id: "google-ads",
    icon: SearchIcon,
    name: "Google Ads",
    tagline: "High-intent traffic that pays back inside 90 days.",
    who: "Brands with a product-market fit signal ready to compound demand.",
    problems: ["Wasted spend on broad match", "Poor SKAG hygiene", "No offline-conversion loop"],
    approach: ["Rebuild account structure", "Introduce Enhanced Conversions + CAPI", "Deploy performance-max with guardrails"],
    outcomes: ["Cleaner attribution", "Higher-quality traffic", "Lower CAC per SKU / service"],
    faqs: [
      { q: "Do you do YouTube ads too?", a: "Yes — including creative testing and shorts-first strategies." },
    ],
  },
  {
    id: "meta-ads",
    icon: Users,
    name: "Meta Ads",
    tagline: "Creative-led performance for D2C and OTT growth.",
    who: "D2C and OTT teams where creative velocity is the bottleneck.",
    problems: ["Creative fatigue", "Poor iOS attribution", "CAC creep"],
    approach: ["Creative testing framework (10+ variants/week)", "Advantage+ with disciplined signals", "Retention layer via Whatsapp + email"],
    outcomes: ["Fresh creative pipeline", "CAC discipline under peak-season pressure", "Better LTV/CAC"],
    faqs: [
      { q: "Do you help with UGC / creators?", a: "Yes — briefing, sourcing and iteration under a live-testing framework." },
    ],
  },
  {
    id: "cro",
    icon: Target,
    name: "Conversion Rate Optimisation (CRO)",
    tagline: "Fix the leak before you pour more traffic in.",
    who: "Teams with traffic but stuck conversion rates.",
    problems: ["Low CVR", "High checkout drop-off", "Broken mobile UX", "Unclear value proposition"],
    approach: ["9-point CRO audit", "Prioritised backlog (ICE-scored)", "A/B test cadence + LP rebuilds"],
    outcomes: ["1.5–3x conversion rate", "Higher AOV via offer engineering", "Better mobile UX"],
    faqs: [
      { q: "How long does a CRO engagement run?", a: "90–120 days minimum for statistically-significant lift on multiple pages." },
    ],
  },
  {
    id: "marketing-automation",
    icon: Bot,
    name: "Marketing Automation",
    tagline: "Email, WhatsApp, and lifecycle systems that lift LTV.",
    who: "D2C and OTT teams leaving repeat revenue on the table.",
    problems: ["No lifecycle flows", "Broken abandonment recovery", "Weak segmentation"],
    approach: ["Segment library + event schema", "Journeys per lifecycle stage", "Automated dashboards"],
    outcomes: ["10–25% incremental revenue from owned channels", "Higher repeat rate", "Automated reporting"],
    faqs: [
      { q: "Which tools do you use?", a: "Klaviyo, Mailmodo, Wati, Interakt — matched to stack + budget." },
    ],
  },
  {
    id: "gtm-analytics",
    icon: BarChart3,
    name: "GTM & Analytics",
    tagline: "Trust your numbers. Ship faster.",
    who: "Any brand where marketing and product data disagree.",
    problems: ["GA4 misconfigured", "Server-side tracking missing", "Attribution chaos"],
    approach: ["GA4 + GTM audit", "Server-side (sGTM) setup", "Looker Studio dashboards"],
    outcomes: ["Single source of truth", "Faster decision cycles", "Cleaner attribution"],
    faqs: [
      { q: "Do you integrate AppsFlyer / Adjust?", a: "Yes — for OTT / mobile app funnels." },
    ],
  },
  {
    id: "ai-marketing-agents",
    icon: Cpu,
    name: "AI Marketing Agents",
    tagline: "Autonomous marketing intelligence for faster decisions.",
    who: "Teams ready to move from dashboards to decisions.",
    problems: ["Data lives in silos", "Slow anomaly detection", "Manual reporting drain"],
    approach: ["Ingest ads + analytics data", "AI agent surfaces anomalies + recommends budget shifts", "Emails a decision-ready daily report"],
    outcomes: ["Autonomous budget reallocation", "30+ decisions/day without manual overrides", "Compounding creative ideas"],
    faqs: [
      { q: "Do you host it or do we?", a: "Either — deployed to your infra or hosted for you on a monthly retainer." },
    ],
  },
];

const ServiceCard = ({ s }) => {
  const [open, setOpen] = useState(false);
  const Icon = s.icon;
  return (
    <motion.div
      {...fadeUp}
      data-testid={`service-${s.id}`}
      id={s.id}
      className={`rounded-2xl border transition ${s.featured ? "bg-[#0F1115] text-white border-white/10" : "bg-white border-neutral-200 hover:border-[#3B82F6]"}`}
    >
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.featured ? "bg-[#3B82F6]/20 text-[#3B82F6]" : "bg-[#3B82F6]/10 text-[#3B82F6]"}`}>
            <Icon size={22} />
          </div>
          {s.featured && (
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#93C5FD] rounded-full border border-[#3B82F6]/30 bg-[#3B82F6]/10 px-3 py-1">
              Flagship
            </span>
          )}
        </div>
        <h3 className={`mt-6 font-display text-2xl md:text-3xl tracking-tight ${s.featured ? "text-white" : "text-neutral-900"}`}>{s.name}</h3>
        <p className={`mt-2 text-sm md:text-base ${s.featured ? "text-white/70" : "text-neutral-500"}`}>{s.tagline}</p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className={`text-[10px] font-mono uppercase tracking-[0.2em] mb-2 ${s.featured ? "text-white/40" : "text-neutral-400"}`}>Who it&apos;s for</div>
            <p className={`text-sm ${s.featured ? "text-white/80" : "text-neutral-700"}`}>{s.who}</p>
          </div>
          <div>
            <div className={`text-[10px] font-mono uppercase tracking-[0.2em] mb-2 ${s.featured ? "text-white/40" : "text-neutral-400"}`}>Outcomes</div>
            <ul className={`text-sm space-y-1 ${s.featured ? "text-white/80" : "text-neutral-700"}`}>
              {s.outcomes.map((o) => (
                <li key={o} className="flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-[#10B981] mt-0.5 shrink-0" /> {o}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className={`text-[10px] font-mono uppercase tracking-[0.2em] mb-2 ${s.featured ? "text-white/40" : "text-neutral-400"}`}>Problems Solved</div>
            <div className="flex flex-wrap gap-2">
              {s.problems.map((p) => (
                <span key={p} className={`text-xs px-2.5 py-1 rounded-full border ${s.featured ? "border-white/15 bg-white/[0.03] text-white/70" : "border-neutral-200 bg-neutral-50 text-neutral-600"}`}>{p}</span>
              ))}
            </div>
          </div>
          <div>
            <div className={`text-[10px] font-mono uppercase tracking-[0.2em] mb-2 ${s.featured ? "text-white/40" : "text-neutral-400"}`}>Approach</div>
            <ol className={`text-sm space-y-1 ${s.featured ? "text-white/80" : "text-neutral-700"}`}>
              {s.approach.map((a, i) => (
                <li key={a} className="flex items-start gap-2">
                  <span className={`text-[10px] font-mono ${s.featured ? "text-white/40" : "text-neutral-400"} shrink-0 w-5 mt-0.5`}>{String(i + 1).padStart(2, "0")}</span>
                  <span>{a}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <button
          data-testid={`service-faq-toggle-${s.id}`}
          onClick={() => setOpen((v) => !v)}
          className={`mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] ${s.featured ? "text-white/60 hover:text-white" : "text-neutral-500 hover:text-neutral-900"}`}
        >
          <ChevronDown size={12} className={`transition ${open ? "rotate-180" : ""}`} />
          {open ? "Hide" : "Show"} FAQs ({s.faqs.length})
        </button>

        {open && (
          <div className="mt-4 space-y-3">
            {s.faqs.map((f, i) => (
              <div key={f.q} data-testid={`service-faq-${s.id}-${i}`} className={`rounded-xl p-4 ${s.featured ? "bg-white/[0.03] border border-white/5" : "bg-neutral-50 border border-neutral-200"}`}>
                <div className={`text-sm font-medium ${s.featured ? "text-white" : "text-neutral-900"}`}>{f.q}</div>
                <div className={`mt-1 text-sm ${s.featured ? "text-white/70" : "text-neutral-600"}`}>{f.a}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={`px-6 md:px-8 py-4 border-t flex items-center justify-between ${s.featured ? "border-white/5 bg-white/[0.02]" : "border-neutral-200 bg-neutral-50"}`}>
        <span className={`text-xs font-mono ${s.featured ? "text-white/50" : "text-neutral-500"}`}>{s.id}</span>
        <Link
          to="/contact"
          data-testid={`service-cta-${s.id}`}
          className={`inline-flex items-center gap-2 text-sm font-medium ${s.featured ? "text-[#93C5FD] hover:text-white" : "text-[#3B82F6] hover:text-[#2563EB]"}`}
        >
          Book a call for {s.name} <ArrowRight size={14} />
        </Link>
      </div>
    </motion.div>
  );
};

export default function Services() {
  useSEO({
    title: "Services · AI Marketing, Performance, CRO, GA4 · Shivani Ranawat",
    description: "AI Growth Marketing Consulting services: AI Marketing Agents, Performance Marketing (Google, Meta, Amazon DSP), CRO, GA4 & GTM, Marketing Automation. Who it's for, problems solved, and expected outcomes.",
    path: "/services",
  });
  useJsonLd([
    breadcrumb([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]),
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Growth Marketing Services",
      itemListElement: services.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          name: s.name,
          description: s.tagline,
          provider: { "@id": "https://marketing-ops-18.emergent.host/#shivani" },
          areaServed: "Worldwide",
        },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: services.flatMap((s) =>
        s.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        }))
      ),
    },
  ]);
  return (
    <div data-testid="services-page">
      <section className="pt-32 pb-16 bg-white border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">Services</div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-7xl tracking-tight text-neutral-900 max-w-4xl leading-[1.02]">
            Eight offerings.<br /> <span className="text-neutral-400">One growth operating system.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-neutral-600 text-base md:text-lg">
            Every engagement combines strategy + hands-on execution. Choose one, or combine as a quarterly sprint.
          </p>
        </div>
      </section>

      <section className="bg-[#F7F7F5] py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 gap-6">
            {services.map((s) => <ServiceCard key={s.id} s={s} />)}
          </div>
        </div>
      </section>
    </div>
  );
}
